import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useGlobalConfig } from "@/contexts/GlobalConfigContext";
import { Header } from "./WorkspacesTab";

const FONTS = ["Inter", "Figtree", "Space Grotesk", "DM Sans", "IBM Plex Sans", "Manrope"];

export default function MasterStylesTab() {
  const { refresh } = useGlobalConfig();
  const [styles, setStyles] = useState(null);
  const [announcement, setAnnouncement] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/super-admin/config");
        setStyles(data.styles);
        setAnnouncement(data.announcement);
      } catch (e) { toast.error("Kon styles niet laden"); }
    })();
  }, []);

  const patchStyles = (p) => { setStyles((s) => ({ ...s, ...p })); setDirty(true); };
  const patchAnn = (p) => { setAnnouncement((a) => ({ ...a, ...p })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/super-admin/config", { styles, announcement });
      toast.success("Opgeslagen — alle tenant hubs volgen deze styles");
      setDirty(false);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  if (!styles) return <div className="text-sm text-gray-500">Laden…</div>;

  return (
    <div data-testid="sa-styles">
      <Header title="Master Styles" sub="Deze keuzes worden via CSS-variabelen in élke tenant hub geïnjecteerd." />

      <section className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white p-6 mb-6">
        <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-4">Design tokens</div>

        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[#1a1a1a]">Base corner radius</label>
              <span className="font-mono text-xs text-gray-500">{styles.radius}px</span>
            </div>
            <input
              type="range" min={0} max={24} step={1}
              value={styles.radius}
              onChange={(e) => patchStyles({ radius: parseInt(e.target.value, 10) })}
              className="w-full accent-[var(--global-action)]"
              data-testid="sa-radius"
            />
            <div className="flex items-center gap-3 mt-3">
              {[0, 4, 8, 12, 16, 24].map((r) => (
                <div key={r} className="flex flex-col items-center gap-1">
                  <div
                    className="w-10 h-10 bg-[#1a1a1a]"
                    style={{ borderRadius: `${r}px` }}
                  />
                  <span className="text-[10px] text-gray-400 font-mono">{r}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-[#1a1a1a] block mb-2">Master action color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={styles.action_color}
                onChange={(e) => patchStyles({ action_color: e.target.value })}
                className="w-14 h-10 rounded-[var(--global-radius)] border border-gray-200 bg-white cursor-pointer"
                data-testid="sa-action-color"
              />
              <Input
                value={styles.action_color}
                onChange={(e) => patchStyles({ action_color: e.target.value })}
                className="w-32 font-mono text-sm bg-white"
              />
              <div
                className="h-10 px-4 rounded-[var(--global-radius)] text-white text-sm font-medium flex items-center"
                style={{ background: styles.action_color }}
              >Voorbeeldknop</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FontPicker label="Sans (lopende tekst)" value={styles.font_sans} onChange={(v) => patchStyles({ font_sans: v })} testId="sa-font-sans" />
            <FontPicker label="Display (koppen)" value={styles.font_display} onChange={(v) => patchStyles({ font_display: v })} testId="sa-font-display" />
          </div>
        </div>
      </section>

      <section className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white p-6 mb-6">
        <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-4">Systeem-aankondiging</div>
        <div className="space-y-3">
          <div className="flex items-center justify-between h-11 px-3 rounded-[var(--global-radius)] border border-gray-200">
            <div className="text-sm">Toon banner bovenaan elke hub</div>
            <Switch checked={!!announcement?.active} onCheckedChange={(v) => patchAnn({ active: v })} data-testid="sa-announcement-active" />
          </div>
          <Input
            value={announcement?.text || ""}
            onChange={(e) => patchAnn({ text: e.target.value })}
            placeholder="Bijv. 'Onderhoud vanavond tussen 20:00 en 21:00'"
            className="bg-white"
            data-testid="sa-announcement-text"
          />
        </div>
      </section>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="sa-styles-save"
          className="h-10 px-6 rounded-[var(--global-radius)] bg-[var(--global-action)] text-white hover:opacity-90 disabled:opacity-50"
        >{saving ? "Opslaan…" : "Opslaan"}</Button>
      </div>
    </div>
  );
}

function FontPicker({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-[#1a1a1a] mb-2">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="bh-select w-full h-10 px-3 rounded-[var(--global-radius)] border border-gray-200 bg-white text-sm"
      >
        {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
      <div className="mt-2 text-sm" style={{ fontFamily: `"${value}", sans-serif` }}>The quick brown fox jumps over the lazy dog</div>
    </label>
  );
}
